if (!customElements.get('wheel-spin-redesign')) {
  const SPIN_SOUND_URL = 'https://cdn.shopify.com/s/files/1/0535/5470/8675/files/gameshow-wheel.mp3?v=1785917930';

  class WheelSpinRedesign extends HTMLElement {
    connectedCallback() {
      const sid = this.dataset.sid;
      const configEl = document.getElementById('wsr-config-' + sid);
      this.config = configEl
        ? JSON.parse(configEl.textContent)
        : { segments: [], spinSeconds: 5.5, loggedIn: false };

      this.loggedIn = !!this.config.loggedIn;
      this.spinsRemaining = 0;
      this.statusLoaded = false;
      this.entriesTotal = 0;
      this.jackpot = null;
      this.jackpotLastHit = null;
      this.jackpotCountedUp = false;
      this.jackpotInView = false;

      this.spinSound = new Audio(SPIN_SOUND_URL);
      this.spinSound.preload = 'auto';

      this.wheelEl = this.querySelector('[data-wheel-ref]');
      this.spinBtn = this.querySelector('[data-spin-btn]');
      this.hubText = this.querySelector('[data-hub-text]');
      this.hubMonogramEl = this.querySelector('[data-hub-monogram]');
      const grid = this.closest('.wsr-grid');
      this.resultCaption = grid.querySelector('[data-result-caption]');
      this.resultValue = grid.querySelector('[data-result-value]');
      this.entriesValueEl = grid.querySelector('[data-entries-value]');
      this.jackpotTickEl = this.querySelector('[data-jackpot-tick]');
      this.jackpotTilesEl = grid.querySelector('[data-jackpot-tiles]');
      this.jackpotLastHitEl = grid.querySelector('[data-jackpot-lasthit]');
      this.setupJackpotObserver();

      this.spinsModal = this.dataset.spinsModalId ? document.getElementById(this.dataset.spinsModalId) : null;
      this.guestModalHeadingEl = this.spinsModal ? this.spinsModal.querySelector('[data-guest-modal-heading]') : null;
      this.guestModalHeadingDefault = this.guestModalHeadingEl ? this.guestModalHeadingEl.textContent : '';
      this.winModal = this.dataset.winModalId ? document.getElementById(this.dataset.winModalId) : null;
      this.winCaptionEl = this.winModal ? this.winModal.querySelector('[data-win-caption]') : null;
      this.winValueEl = this.winModal ? this.winModal.querySelector('[data-win-value]') : null;
      this.winEntriesEl = this.winModal ? this.winModal.querySelector('[data-win-entries]') : null;
      this.winNoticeEl = this.winModal ? this.winModal.querySelector('[data-win-notice]') : null;
      this.winSparksEl = this.winModal ? this.winModal.querySelector('[data-win-sparks]') : null;

      this.segBigEls = Array.from(this.querySelectorAll('[data-seg-big]'));
      this.segCapEls = Array.from(this.querySelectorAll('[data-seg-cap]'));
      this.segLabelEls = Array.from(this.querySelectorAll('[data-seg-label]'));
      this.packCards = Array.from(document.querySelectorAll('[data-pack-card][data-wsr-sid="' + sid + '"]'));
      this.packStatusEl = grid.querySelector('[data-pack-status]');
      this.packsListEl = grid.querySelector('[data-pack-list]');
      this.packsPrevBtn = grid.querySelector('[data-packs-prev]');
      this.packsNextBtn = grid.querySelector('[data-packs-next]');
      this.packsDots = Array.from(grid.querySelectorAll('[data-packs-dot]'));
      this.setupPacksSlider();

      this.rot = 0;
      this.spinning = false;
      this.selectedPack = this.packCards.findIndex((c) => c.classList.contains('is-selected'));
      if (this.selectedPack < 0) this.selectedPack = 0;

      this.spinBtn.addEventListener('click', () => this.spin());
      this.packCards.forEach((card, i) => {
        const activate = () => {
          this.selectPack(card);
          this.addPackToCart(card);
        };
        card.addEventListener('click', activate);
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activate();
          }
        });
      });

      // Liquid can't comma-format the computed multiplier (e.g. "1,000X")
      // server-side, so re-render once immediately with JS's fmt().
      this.renderSegments();
      grid.querySelectorAll('[data-guaranteed-entries]').forEach((el) => {
        const amount = parseInt(el.dataset.guaranteedEntries, 10);
        if (Number.isNaN(amount)) return;
        el.textContent = this.fmt(amount) + ' MIN. GUARANTEED ENTRIES';
      });
      if (this.spinsModal) {
        this.spinsModal.querySelectorAll('[data-guaranteed-entries]').forEach((el) => {
          const amount = parseInt(el.dataset.guaranteedEntries, 10);
          if (Number.isNaN(amount)) return;
          el.textContent = this.fmt(amount) + ' ENTRIES';
        });
      }

      this.fetchStatus();
    }

    disconnectedCallback() {
      clearTimeout(this.resultTimer);
      clearTimeout(this.resultRevertTimer);
      clearTimeout(this.packStatusTimer);
      clearTimeout(this.packsScrollTimer);
      if (this.jackpotObserver) this.jackpotObserver.disconnect();
      this.spinSound.pause();
    }

    /** Defers the jackpot count-up until its display actually scrolls into view. */
    setupJackpotObserver() {
      if (!this.jackpotTilesEl || typeof IntersectionObserver === 'undefined') {
        this.jackpotInView = true;
        return;
      }
      this.jackpotObserver = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        this.jackpotInView = true;
        this.jackpotObserver.disconnect();
        if (this.jackpot !== null && !this.jackpotCountedUp) {
          this.jackpotCountedUp = true;
          this.animateJackpotCountUp(this.jackpot);
        }
      }, { threshold: 0.4 });
      this.jackpotObserver.observe(this.jackpotTilesEl);
    }

    /** Mobile pack-card slider: arrow/dot controls drive the same scroll-snap list a swipe would. */
    setupPacksSlider() {
      if (!this.packsListEl) return;
      const cards = Array.from(this.packsListEl.children);
      if (!cards.length) return;

      this.packsActiveIndex = 0;

      const scrollToIndex = (i) => {
        const target = Math.max(0, Math.min(cards.length - 1, i));
        cards[target].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      };

      const updateActiveDot = () => {
        const listRect = this.packsListEl.getBoundingClientRect();
        const center = listRect.left + listRect.width / 2;
        let closest = 0;
        let closestDist = Infinity;
        cards.forEach((card, i) => {
          const rect = card.getBoundingClientRect();
          const dist = Math.abs(rect.left + rect.width / 2 - center);
          if (dist < closestDist) {
            closestDist = dist;
            closest = i;
          }
        });
        this.packsActiveIndex = closest;
        this.packsDots.forEach((dot, i) => dot.classList.toggle('is-active', i === closest));
        if (this.packsPrevBtn) this.packsPrevBtn.disabled = closest === 0;
        if (this.packsNextBtn) this.packsNextBtn.disabled = closest === cards.length - 1;
      };

      if (this.packsPrevBtn) this.packsPrevBtn.addEventListener('click', () => scrollToIndex(this.packsActiveIndex - 1));
      if (this.packsNextBtn) this.packsNextBtn.addEventListener('click', () => scrollToIndex(this.packsActiveIndex + 1));
      this.packsDots.forEach((dot, i) => dot.addEventListener('click', () => scrollToIndex(i)));

      this.packsListEl.addEventListener(
        'scroll',
        () => {
          clearTimeout(this.packsScrollTimer);
          this.packsScrollTimer = setTimeout(updateActiveDot, 100);
        },
        { passive: true }
      );

      updateActiveDot();
    }

    fmt(n) {
      return n.toLocaleString('en-US');
    }

    /**
     * Pulls the customer's real spins/entries/jackpot from the backend on
     * load and after every spin. Guests get a 401 here (status.php requires
     * a session) — that's expected, and we just leave the server-rendered
     * "—" placeholders in place rather than show a made-up number.
     */
    async fetchStatus() {
      try {
        const response = await fetch('/apps/wheel-spin/status', {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;
        const data = await response.json();
        this.statusLoaded = true;

        if (typeof data.spinsRemaining === 'number') {
          this.spinsRemaining = data.spinsRemaining;
          this.renderIdleResult();
        }
        if (typeof data.entriesTotal === 'number') {
          this.entriesTotal = data.entriesTotal;
          if (this.entriesValueEl) this.entriesValueEl.textContent = this.fmt(this.entriesTotal);
        }
        if (typeof data.jackpotCurrentValue === 'number') {
          this.jackpot = data.jackpotCurrentValue;
          this.jackpotLastHit = data.jackpotLastHit || null;
          this.renderJackpot();
        }
      } catch (error) {
        // Keep the server-rendered placeholders if the status check fails.
      }
    }

    renderJackpot() {
      if (this.jackpot === null) return;
      if (this.jackpotTickEl) this.jackpotTickEl.textContent = this.fmt(this.jackpot);
      this.renderJackpotTiles();
      this.renderJackpotLastHit();
    }

    /** Updates the jackpot value display: counts up from 0 once its display scrolls into view, flashes on later changes. */
    renderJackpotTiles() {
      if (!this.jackpotTilesEl) return;

      if (!this.jackpotCountedUp) {
        if (!this.jackpotInView) return;
        this.jackpotCountedUp = true;
        this.animateJackpotCountUp(this.jackpot);
        return;
      }

      const formatted = this.fmt(this.jackpot);
      if (this.jackpotTilesEl.textContent === formatted) return;

      this.jackpotTilesEl.textContent = formatted;
      this.jackpotTilesEl.classList.remove('is-flipping');
      void this.jackpotTilesEl.offsetWidth; // force reflow so the animation can retrigger
      this.jackpotTilesEl.classList.add('is-flipping');
    }

    /** Counts the jackpot display up from 0 to its real value on first load. */
    animateJackpotCountUp(target) {
      const el = this.jackpotTilesEl;
      const dur = 1400;
      const start = performance.now();

      const tick = (now) => {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 3);
        el.textContent = this.fmt(Math.round(eased * target));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }

    renderJackpotLastHit() {
      if (!this.jackpotLastHitEl) return;

      if (!this.jackpotLastHit) {
        this.jackpotLastHitEl.textContent = '';
        return;
      }

      const { amount, customerDisplayName, wonAt } = this.jackpotLastHit;
      const who = customerDisplayName ? ' TO ' + customerDisplayName.toUpperCase() : '';
      this.jackpotLastHitEl.textContent = 'LAST HIT · ' + this.daysAgo(wonAt) + ' · ' + this.fmt(amount) + who;
    }

    daysAgo(mysqlDateTime) {
      const then = new Date(mysqlDateTime.replace(' ', 'T'));
      const days = Math.floor((Date.now() - then.getTime()) / 86400000);
      if (days <= 0) return 'TODAY';
      if (days === 1) return '1 DAY AGO';
      return days + ' DAYS AGO';
    }

    /** Idle (not spinning, not showing a just-landed result) state of the result panel. */
    renderIdleResult() {
      if (!this.loggedIn || this.spinning) return;
      if (this.resultCaption) this.resultCaption.textContent = 'SPINS LEFT';
      if (this.resultValue) this.resultValue.textContent = String(this.spinsRemaining);
    }

    /** Briefly show a message in the result panel, then revert to the idle state. */
    flashResult(caption, value) {
      if (this.resultCaption) this.resultCaption.textContent = caption;
      if (this.resultValue) this.resultValue.textContent = value;
      clearTimeout(this.resultRevertTimer);
      this.resultRevertTimer = setTimeout(() => this.renderIdleResult(), 2500);
    }

    /**
     * Shown for guests (to sell a login, optionally with what a demo spin
     * just won) and for logged-in customers out of spins (login link hidden
     * via Liquid).
     */
    openSpinsModal(demoPrize) {
      if (this.guestModalHeadingEl) {
        this.guestModalHeadingEl.textContent = demoPrize ? 'You could have won: ' + demoPrize + '!' : this.guestModalHeadingDefault;
      }
      if (this.spinsModal && typeof this.spinsModal.show === 'function') {
        this.spinsModal.show(this.spinBtn);
      }
    }

    selectPack(card) {
      this.packCards.forEach((c) => c.classList.toggle('is-selected', c === card));
    }

    /**
     * Mirrors WheelSpin.onPackAdd (assets/wheel-spin.js) — the theme's own
     * pattern for card-based multi-instance add-to-cart, since the shared
     * <product-form> element assumes one form per section.
     */
    addPackToCart(card) {
      if (card.classList.contains('is-loading')) return;
      const variantId = card.dataset.variantId;
      if (!variantId) {
        if (Shopify && Shopify.designMode) {
          this.setPackStatus('No product assigned to this pack yet — set one in the theme editor.', 'error');
        }
        return;
      }

      card.classList.add('is-loading');
      card.setAttribute('aria-disabled', 'true');
      this.setPackStatus('', null);

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      formData.append('sections', this.getCartSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);

      fetch(theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
        body: formData,
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            throw new Error(response.message || 'cart_add_failed');
          }

          this.renderCartSections(response);

          if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent('cart:item-added', {
              product: response.hasOwnProperty('items') ? response.items[0] : response,
            });
          }

          this.setPackStatus('Added to cart', 'success');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error);
          this.setPackStatus('Could not add to cart', 'error');
        })
        .finally(() => {
          card.classList.remove('is-loading');
          card.removeAttribute('aria-disabled');
        });
    }

    getCartSectionsToRender() {
      return [
        { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' },
      ];
    }

    renderCartSections(parsedState) {
      this.getCartSectionsToRender().forEach((section) => {
        const container = document.getElementById(section.id);
        if (!container) return;

        const target = container.querySelector(section.selector) || container;
        target.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        if (section.id === 'Cart-Drawer' && typeof container.removeProductEvent === 'function') {
          container.removeProductEvent();
        }
      });

      if (typeof Cart !== 'undefined') {
        new Cart().renderContents(parsedState);
      }

      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    setPackStatus(message, state) {
      if (!this.packStatusEl) return;
      clearTimeout(this.packStatusTimer);
      this.packStatusEl.textContent = message;
      if (state) {
        this.packStatusEl.dataset.state = state;
      } else {
        delete this.packStatusEl.dataset.state;
      }
      if (message) {
        this.packStatusTimer = setTimeout(() => {
          this.packStatusEl.textContent = '';
        }, 2500);
      }
    }

    renderSegments() {
      const segments = this.config.segments;
      this.segBigEls.forEach((el, i) => {
        const seg = segments[i];
        if (!seg) return;

        let big;
        let small;
        if (seg.kind === 'bonus') {
          big = '+1';
          small = 'BONUS SPIN';
        } else if (seg.kind === 'pack') {
          big = seg.packLabel;
          small = 'PACK';
        } else {
          const multiplier = Math.round(seg.amount / 25);
          big = multiplier + 'X';
          small = 'MULTIPLIER';
        }
        // Customizer-entered overrides win outright, whatever the kind.
        if (seg.bigOverride) big = seg.bigOverride;
        if (seg.capOverride) small = seg.capOverride;

        el.textContent = big;
        el.style.fontSize = (big.length >= 7 ? 22 : big.length >= 5 ? 27 : 31) + 'px';
        if (this.segCapEls[i]) this.segCapEls[i].textContent = small;
      });
    }

    spin() {
      if (!this.loggedIn) {
        this.runDemoSpin();
        return;
      }

      if (this.spinning) return;

      if (this.statusLoaded && this.spinsRemaining <= 0) {
        this.openSpinsModal();
        return;
      }

      this.spinning = true;
      this.hubText.textContent = 'SPINNING…';
      this.resultCaption.textContent = 'SPINNING';
      this.resultValue.textContent = '—';

      this.requestSpin()
        .then((result) => this.runSpinAnimation(result))
        .catch((error) => {
          this.spinning = false;
          this.hubText.textContent = 'SPIN';

          if (error && error.status === 409) {
            this.spinsRemaining = 0;
            this.renderIdleResult();
            this.openSpinsModal();
          } else if (error && error.status === 429) {
            this.flashResult('SLOW DOWN', 'TRY AGAIN SOON');
          } else {
            this.flashResult('SOMETHING WENT WRONG', 'TRY AGAIN');
          }

          this.fetchStatus();
        });
    }

    /**
     * Consumes a spin via the wheel-spin backend (Shopify App Proxy ->
     * app.speedsociety.com). The prize is rolled server-side — this never
     * determines the outcome locally.
     */
    async requestSpin() {
      const response = await fetch('/apps/wheel-spin/spin', {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const error = new Error(data.error || 'request_failed');
        error.status = response.status;
        throw error;
      }

      return data;
    }

    runSpinAnimation(result) {
      this.animateToSegment(result.segmentIndex).then(() => this.applyResult(result));
    }

    /**
     * Guests have no real spin to consume, so this rolls a segment locally,
     * plays the exact same wheel animation as a real spin, then pitches the
     * login/pack modal with what that spin would have won.
     */
    runDemoSpin() {
      if (this.spinning) return;
      this.spinning = true;

      const i = Math.floor(Math.random() * 10);
      this.animateToSegment(i).then(() => {
        this.spinning = false;
        this.hubText.textContent = 'SPIN';
        this.openSpinsModal(this.describeDemoPrize(i));
      });
    }

    describeDemoPrize(i) {
      if (i === 9) {
        return this.jackpot !== null ? this.fmt(this.jackpot) + ' PROGRESSIVE JACKPOT' : 'THE PROGRESSIVE JACKPOT';
      }
      const seg = this.config.segments[i];
      if (!seg) return '';
      if (seg.kind === 'bonus') return '+1 BONUS SPIN';
      if (seg.kind === 'pack') return seg.packLabel + ' PACK';
      return this.fmt(seg.amount) + ' ENTRIES';
    }

    /** Spins the wheel to segment i and resolves once it's fully stopped (shared by real and demo spins). */
    animateToSegment(i) {
      const dur = Math.round((this.config.spinSeconds || 5.5) * 1000);
      const cur = this.rot;
      const target = cur + (5 + Math.floor(Math.random() * 3)) * 360 + (((-36 * i - cur) % 360) + 360) % 360;
      const transition = 'transform ' + dur + 'ms cubic-bezier(0.14, 0.72, 0.02, 1)';

      this.wheelEl.style.transition = transition;
      this.wheelEl.style.transform = 'rotate(' + target + 'deg)';
      this.rot = target;

      // 40 LEDs animating individually on top of the wheel's own rotation
      // is a lot of concurrently composited layers on mobile GPUs; pausing
      // them during the spin (they're a blur at speed anyway) cuts that
      // load during the highest-risk window.
      this.wheelEl.classList.add('is-spinning');

      // Labels and the hub monogram sit inside the rotating wheel but must
      // stay upright, so each counter-rotates by the wheel's rotation using
      // the same transition.
      this.segLabelEls.forEach((label, idx) => {
        label.style.transition = transition;
        label.style.transform = 'rotate(' + (-(idx * 36) - target) + 'deg)';
      });
      if (this.hubMonogramEl) {
        this.hubMonogramEl.style.transition = transition;
        this.hubMonogramEl.style.transform = 'rotate(' + -target + 'deg)';
      }

      this.spinSound.currentTime = 0;
      this.spinSound.play().catch(() => {});

      this.hubText.textContent = 'SPINNING…';

      clearTimeout(this.resultTimer);

      return new Promise((resolve) => {
        this.resultTimer = setTimeout(() => {
          this.wheelEl.classList.remove('is-spinning');
          resolve();
        }, dur + 80);
      });
    }

    applyResult(result) {
      this.spinning = false;
      this.hubText.textContent = 'SPIN';

      const isJackpot = result.prizeType === 'jackpot';
      this.resultCaption.textContent = isJackpot ? 'JACKPOT HIT' : 'LANDED ON';
      this.resultValue.textContent = result.prizeLabel || result.label || '—';

      this.spinsRemaining = result.spinsRemaining;
      this.entriesTotal = result.entriesTotal;
      if (this.entriesValueEl) this.entriesValueEl.textContent = this.fmt(this.entriesTotal);

      clearTimeout(this.resultRevertTimer);
      this.resultRevertTimer = setTimeout(() => this.renderIdleResult(), 4000);

      this.showWinModal(result, isJackpot);

      // The spin response doesn't carry the jackpot's current value (it only
      // changes server-side); resync it separately now that it may have grown.
      this.fetchStatus();
    }

    showWinModal(result, isJackpot) {
      if (!this.winModal || typeof this.winModal.show !== 'function') return;

      if (this.winCaptionEl) {
        this.winCaptionEl.textContent = isJackpot
          ? 'JACKPOT!'
          : result.prizeType === 'bonus_spin'
          ? 'BONUS SPIN!'
          : result.prizeType === 'free_product'
          ? 'FREE PRIZE!'
          : 'YOU WON';
      }
      if (this.winValueEl) this.winValueEl.textContent = result.prizeLabel || result.label || '—';
      if (this.winEntriesEl) {
        if (result.entriesWon) {
          this.winEntriesEl.textContent = '+' + this.fmt(result.entriesWon) + ' ENTRIES';
        } else if (result.prizeType === 'bonus_spin') {
          this.winEntriesEl.textContent = '+1 SPIN';
        } else {
          this.winEntriesEl.textContent = '';
        }
      }
      if (this.winNoticeEl) this.winNoticeEl.hidden = result.prizeType !== 'free_product';

      this.winModal.show(this.spinBtn);
      this.renderWinSparks(this.winSparksEl);
    }

    clearSparks(container) {
      if (container) container.innerHTML = '';
    }

    /** Sparks radiating outward in all directions, for the win modal. */
    renderWinSparks(container) {
      const target = container;
      if (!target) return;
      this.clearSparks(target);
      const n = 36;
      const frag = document.createDocumentFragment();
      for (let k = 0; k < n; k++) {
        const angle = (k / n) * Math.PI * 2 + Math.random() * 0.3;
        const reach = 100 + Math.random() * 140;
        const dx = Math.round(Math.cos(angle) * reach);
        const dy = Math.round(Math.sin(angle) * reach);
        const size = 2 + Math.round(Math.random() * 3);
        const dur = 500 + Math.round(Math.random() * 500);
        const delay = Math.round(Math.random() * 200);
        const spark = document.createElement('div');
        spark.className = 'wsr-spark';
        spark.style.width = size + 'px';
        spark.style.height = size + 'px';
        spark.style.setProperty('--dx', dx + 'px');
        spark.style.setProperty('--dy', dy + 'px');
        spark.style.animationDuration = dur + 'ms';
        spark.style.animationDelay = delay + 'ms';
        frag.appendChild(spark);
      }
      target.appendChild(frag);
    }
  }

  customElements.define('wheel-spin-redesign', WheelSpinRedesign);
}
